import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, LayersFront, Pencil, Plus } from '@factorialco/f0-react/icons/app'
import { OverviewDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import app from 'modules/core/store/models/app'

import DeleteJobCatalogTreeNodeModal from 'modules/job_catalog/components/DeleteJobCatalogTreeNodeModal'
import NodeModal from 'modules/job_catalog/components/NodeModal'
import useCreateTreeWithChildren from 'modules/job_catalog/hooks/useCreateTreeWithChildren'
import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'
import { useNodeModalContent } from 'modules/job_catalog/hooks/useModalContent'
import { useNodeColumns } from 'modules/job_catalog/hooks/useNodeColumns'
import useUpdateJobCatalogTreeNode from 'modules/job_catalog/hooks/useUpdateJobCatalogTreeNode'
import {
  GroupedData,
  NODE_TYPES,
  NodeChildren,
  NodeFormValues,
  TreeNode,
  findNodeByUuid,
  formatUuidToNodeType,
  getChildNodeType,
  getChildTypeText,
  getNavigationForTypeFromFunction,
  getNodeId,
  getOverviewActionLabel,
  getOverviewEmptyStateContent,
  getSalariesFromChildAttributes,
  getTextFromNodeType,
  getValidNodeIdFromParams,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'
import { formatSalariesFromCentsObject } from 'modules/job_catalog/lib/salaryUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const extractLevels = (nodes: TreeNode[]): string[] =>
  nodes.filter((n) => normalizeNodeType(n.type) === NODE_TYPES.LEVEL).map((n) => n.name)

const mapChildrenData = (
  childrenData: NodeChildren[],
  node: TreeNode | null,
  setNodeName: (name: string) => void
): GroupedData[] => {
  if (!node) {
    return []
  }

  setNodeName(node.name)
  const nodeType = normalizeNodeType(node.type)

  const mappedData = childrenData.map((child) => {
    const baseData = {
      uuid: `${child.type}-${child.id.toString()}`,
      name: child.name || '',
      salary:
        formatSalariesFromCentsObject(getSalariesFromChildAttributes(child.attributesJson)) || '-',
    }

    if (nodeType === NODE_TYPES.FUNCTION) {
      const roleNode = node.children.find((r) => r.name === child.name)
      return {
        ...baseData,
        levels: roleNode ? extractLevels(roleNode.children || []) : [],
      }
    }

    return baseData
  })

  return mappedData
}

const OverviewPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { rootId, familyId, functionId, roleId, levelId } = useParams()
  const id = getValidNodeIdFromParams(levelId, roleId, functionId, familyId, rootId)
  const [nodeName, setNodeName] = useState<string>('')
  const [nodeModalType, setNodeModalType] = useState<string | null>(null)
  const [nodeModalInitialValues, setNodeModalInitialValues] = useState<NodeFormValues | null>(null)
  const [totalChildren, setTotalChildren] = useState<number>(0)
  const [deleteNodeTarget, setDeleteNodeTarget] = useState<{
    uuid: string
    name: string
  } | null>(null)
  const [editNodeTarget, setEditNodeTarget] = useState<{
    uuid: string
    name: string
    description: string
  } | null>(null)
  const treeNodesRef = useRef<TreeNode[]>([])
  const childType = useMemo(() => getChildNodeType(id), [id])
  const { company } = useCurrentSession()
  const { canManageTree } = useJobCatalogTreeVisibility()

  useEffect(() => {
    if (!id) return
    track(trackingEvents.jobCatalog.navigateNodeSubmit, {
      event_category: 'Organizations',
      node_type: normalizeNodeType(id),
      node_id: id,
      tab: 'overview',
    })
  }, [id])
  const { createTree, loading: loadingCreateNode } = useCreateTreeWithChildren()
  const { updateNode, loading: loadingUpdateNode } = useUpdateJobCatalogTreeNode()
  const modalContent = useNodeModalContent(nodeModalType, nodeName, childType)
  const editModalContent = useNodeModalContent('edit', editNodeTarget?.name || '', childType)

  const redirectParams = useMemo(() => {
    return (type: string, uuid: string) => ({
      rootId: rootId || '',
      familyId: familyId || '',
      functionId: functionId || '',
      roleId: roleId || '',
      levelId: levelId || '',
      ...(type === NODE_TYPES.FUNCTION && { functionId: uuid }),
      ...(type === NODE_TYPES.ROLE && { roleId: uuid }),
      ...(type === NODE_TYPES.LEVEL && { levelId: uuid }),
      ...(type === NODE_TYPES.FAMILY && { familyId: uuid }),
    })
  }, [rootId, familyId, functionId, roleId, levelId])

  const source = useGraphqlDataCollectionSource(
    {
      document: OverviewDataCollectionDocument,
      fetchPolicy: 'network-only',
      perPage: 10,
      totalItemSummary: (totalItems: number) => `${getChildTypeText(id, totalItems).toLowerCase()}`,
      mapData: (data) => {
        const rawTree = data?.jobCatalog.treesConnection.nodes[0]?.serializedTree ?? '[]'
        const childrenData = data?.jobCatalog.treeNodesConnection.nodes || []
        const parsed = JSON.parse(rawTree)
        const tree: TreeNode[] = Array.isArray(parsed) ? parsed : [parsed]
        const node = findNodeByUuid(tree, id)
        treeNodesRef.current = tree

        setTotalChildren(data?.jobCatalog.treeNodesConnection.totalCount ?? 0)
        const mappedChildrenData: NodeChildren[] = childrenData.map((child) => ({
          id: child.id,
          name: child.name || '',
          type: child.type || '',
          attributesJson: child.attributesJson || '[]',
        }))

        return mapChildrenData(mappedChildrenData, node, setNodeName)
      },
      mapPagination: (data) => ({
        total: data.jobCatalog.treeNodesConnection.totalCount ?? 0,
      }),
      search: {
        enabled: true,
      },
      sortings: {
        name: {
          label: getChildTypeText(id),
        },
      },
      defaultSortings: {
        field: 'name',
        order: 'asc',
      },
      itemUrl: (item: GroupedData) => {
        const type = normalizeNodeType(item.uuid?.split('-')[0] || '')
        const nav = getNavigationForTypeFromFunction(navigation, type)
        return nav.fullPath(redirectParams(type, item.uuid))
      },
      itemActions: (item) => {
        const nodeTypeName = getTextFromNodeType(item.uuid).toLowerCase()
        return canManageTree
          ? [
              {
                label: i18n.t('common.edit_type', { type: nodeTypeName }),
                icon: Pencil,
                onClick: () => {
                  const childNode = findNodeByUuid(treeNodesRef.current, item.uuid)
                  setEditNodeTarget({
                    uuid: item.uuid,
                    name: item.name,
                    description: childNode?.description || '',
                  })
                },
              },
              {
                label: i18n.t('common.duplicate_type', { type: nodeTypeName }),
                icon: LayersFront,
                onClick: () => {
                  setNodeModalType('duplicate')
                  setNodeModalInitialValues({
                    name: item.name,
                    description: '',
                  })
                },
              },
              { type: 'separator' },
              {
                label: i18n.t('common.delete_type', { type: nodeTypeName }),
                icon: Delete,
                critical: true,
                onClick: () => {
                  setDeleteNodeTarget({ uuid: item.uuid, name: item.name })
                },
              },
            ]
          : []
      },
      primaryActions:
        totalChildren && canManageTree
          ? () => ({
              label: getOverviewActionLabel(normalizeNodeType(childType || '')),
              onClick: () => setNodeModalType('create'),
              icon: Plus,
            })
          : undefined,
      mapVariables: ({ search, sortings }) => ({
        search,
        sortOrder: sortings?.[0],
        nodeUuid: id ?? '',
        childType: formatUuidToNodeType(childType || ''),
      }),
    },
    [id, childType]
  )

  const columns = useNodeColumns(String(id))

  useEffect(() => {
    source.setIsLoading(loadingCreateNode || loadingUpdateNode)
  }, [loadingCreateNode, loadingUpdateNode, source])

  const handleDeleteNode = useCallback((success: boolean) => {
    setDeleteNodeTarget(null)
  }, [])

  const handleEditNode = useCallback(
    async ({ name: editedName, description: editedDescription }: NodeFormValues) => {
      if (!editNodeTarget) return

      const nodeId = getNodeId(editNodeTarget.uuid)
      const nodeType = formatUuidToNodeType(editNodeTarget.uuid)
      const parentId = getNodeId(id)
      const result = await updateNode(
        company.id,
        editedDescription,
        editedName,
        nodeId,
        nodeType,
        parentId,
        editNodeTarget.uuid
      )

      const handled = handleResult(result, (errors) => {
        app.setFlash({
          type: 'wrong',
          message: errors.errors || i18n.t('errors.generic_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('job_catalog.nodes.modal.edit_success', {
            type: getTextFromNodeType(editNodeTarget.uuid),
          }),
        })
      }

      setEditNodeTarget(null)
    },
    [editNodeTarget, id, company.id, updateNode]
  )

  const handleCreateNode = async ({ name, description, children }: NodeFormValues) => {
    const result = await createTree({
      name,
      description,
      children,
      parentUuid: id,
      nodeType: formatUuidToNodeType(childType || ''),
      companyId: company.id,
    })

    if (result.success && result.newNodeUuid) {
      const normalized = normalizeNodeType(childType)
      const nav = getNavigationForTypeFromFunction(navigation, normalized)
      navigate(nav.fullPath(redirectParams(normalized, result.newNodeUuid)))
      setNodeModalType(null)
    }
  }

  return (
    <ResponsivePage.Body variant="full">
      <OneDataCollection
        key={`overview-${id}`}
        source={source}
        emptyStates={{
          'no-data': getOverviewEmptyStateContent({
            type: normalizeNodeType(childType || ''),
            handleAction: () => setNodeModalType('create'),
            canShowAction: canManageTree,
          }),
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns,
              frozenColumns: columns.length > 1 ? 1 : 0,
            },
          },
        ]}
      />
      {nodeModalType && (
        <NodeModal
          onClose={() => setNodeModalType(null)}
          title={modalContent.title}
          description={modalContent.description}
          onSubmit={handleCreateNode}
          icon={modalContent.icon || 'input-field'}
          nodeType={childType || ''}
          initialValues={nodeModalInitialValues ?? undefined}
        />
      )}
      {deleteNodeTarget && (
        <DeleteJobCatalogTreeNodeModal
          nodeId={deleteNodeTarget.uuid}
          nodeName={deleteNodeTarget.name}
          nodeType={normalizeNodeType(deleteNodeTarget.uuid.split('-')[0] || '')}
          childrenNodeType={getChildNodeType(deleteNodeTarget.uuid)}
          onSubmit={handleDeleteNode}
          onClose={() => setDeleteNodeTarget(null)}
          canBeDeleted
        />
      )}
      {editNodeTarget && (
        <NodeModal
          onClose={() => setEditNodeTarget(null)}
          title={editModalContent.title}
          description={editModalContent.description}
          onSubmit={handleEditNode}
          icon={editModalContent.icon || 'pencil'}
          nodeType={childType || ''}
          initialValues={{
            name: editNodeTarget.name,
            description: editNodeTarget.description,
            children: [],
          }}
          actionType="edit"
        />
      )}
    </ResponsivePage.Body>
  )
}

export default OverviewPage
